const ESC = 0x1B;
const GS = 0x1D;
const FS = 0x1C;
const LF = 0x0A;

export type PaperWidth = "58mm" | "80mm";

export function getCharsPerLine(width: PaperWidth): number {
  return width === "58mm" ? 32 : 48;
}

export function cmd(...bytes: number[]): Uint8Array {
  return new Uint8Array(bytes);
}

export const ESC_POS = {
  INIT: cmd(ESC, 0x40),
  // FS . (1C 2E) = cancela modo Kanji/CJK. Vai logo DEPOIS do INIT (o `ESC @`
  // reseta pro default de fábrica, que num clone com boot em GBK pode re-religar
  // o modo multibyte) e ANTES do codepage. Sem isso, esse clone consome bytes
  // altos em PARES — o ã engole a próxima letra / vira ideograma ("Impressão"→
  // "Impressú"). É defeito ORTOGONAL ao do codepage (aquele é glifo trocado por
  // tabela ausente; este é pair-eating por modo multibyte). No-op em Epson-
  // compatible real → defesa de header segura pra frota toda. Antes morava só no
  // agente Android (withKanjiOff client-side); centralizado aqui pra cobrir TODOS
  // os agentes (Windows é passthrough puro, herda de graça). Ver [[project_escpos_cp858]].
  KANJI_OFF: cmd(FS, 0x2E),
  // CP850 (PC850 Multilingual) = tabela 2. Cobre TODO o acento do português
  // (ç ã õ á é í ó ú â ê ô à) e é a page mais universal entre térmicas ESC/POS,
  // name-brand E clone barato. Histórico das tentativas (não regredir de novo):
  //   - CP1252 (tabela 16): Epson-only; clone ignora → acento trocado.
  //   - CP850 (tabela 2): universal; rodou bem em toda a frota.
  //   - CP858 (tabela 19): = CP850 + € no slot 0xD5. Migrado em jun/2026 só pra
  //     ganhar €, MAS clones baratos (Knup IM607, Goojprt, ZJ…) não têm a tabela
  //     19 no firmware: ignoram o `ESC t 19`, ficam na page default e o cupom sai
  //     com acento trocado ("Linguiça" → glifo errado). Revertido pra tabela 2.
  // € foi sacrificado de propósito: cupom BR usa R$ (ASCII), € nunca aparece.
  // Trocar este comando exige rever o encoder (encodeCP850Char) — andam casados.
  CODEPAGE_CP850: cmd(ESC, 0x74, 0x02),
  LF: cmd(LF),
  BOLD_ON: cmd(ESC, 0x45, 0x01),
  BOLD_OFF: cmd(ESC, 0x45, 0x00),
  DOUBLE_ON: cmd(ESC, 0x21, 0x30),
  DOUBLE_OFF: cmd(ESC, 0x21, 0x00),
  // Altura dupla com a largura normal (a «letra alta»): a quebra de linha não muda.
  TALL_ON: cmd(ESC, 0x21, 0x10),
  ALIGN_LEFT: cmd(ESC, 0x61, 0x00),
  ALIGN_CENTER: cmd(ESC, 0x61, 0x01),
  ALIGN_RIGHT: cmd(ESC, 0x61, 0x02),
  CUT: cmd(GS, 0x56, 0x00),
  PARTIAL_CUT: cmd(GS, 0x56, 0x01),
  // GS V B 0: avanca ATE A POSICAO DE CORTE e corta. Diferente do GS V 1, que
  // corta na posicao atual e por isso obriga a alimentar papel na mao pra lamina
  // nao passar no meio do texto. Aqui a propria impressora sabe a distancia
  // cabeca->lamina, entao o rodape nao precisa chutar folga.
  CUT_FEED: cmd(GS, 0x56, 66, 0x00),
  FEED_2: cmd(ESC, 0x64, 0x02),
  FEED_3: cmd(ESC, 0x64, 0x03),
  FEED_5: cmd(ESC, 0x64, 0x05),
};

// Unicode → byte na code page 850 (PC850 Multilingual). Cobre todo o Latin-1
// acentuado do português + símbolos comuns de cupom. ASCII (<=0x7F) passa
// direto e não entra aqui. O slot 0xD5 é `ı` (dotless i) em CP850 — por isso
// € (U+20AC) NÃO entra neste mapa (cai no fallback '?', nunca aparece em BR).
const UNICODE_TO_CP850: Record<number, number> = {
  0x00C7: 0x80, 0x00FC: 0x81, 0x00E9: 0x82, 0x00E2: 0x83,
  0x00E4: 0x84, 0x00E0: 0x85, 0x00E5: 0x86, 0x00E7: 0x87,
  0x00EA: 0x88, 0x00EB: 0x89, 0x00E8: 0x8A, 0x00EF: 0x8B,
  0x00EE: 0x8C, 0x00EC: 0x8D, 0x00C4: 0x8E, 0x00C5: 0x8F,
  0x00C9: 0x90, 0x00E6: 0x91, 0x00C6: 0x92, 0x00F4: 0x93,
  0x00F6: 0x94, 0x00F2: 0x95, 0x00FB: 0x96, 0x00F9: 0x97,
  0x00FF: 0x98, 0x00D6: 0x99, 0x00DC: 0x9A, 0x00F8: 0x9B,
  0x00A3: 0x9C, 0x00D8: 0x9D, 0x00D7: 0x9E, 0x0192: 0x9F,
  0x00E1: 0xA0, 0x00ED: 0xA1, 0x00F3: 0xA2, 0x00FA: 0xA3,
  0x00F1: 0xA4, 0x00D1: 0xA5, 0x00AA: 0xA6, 0x00BA: 0xA7,
  0x00BF: 0xA8, 0x00AE: 0xA9, 0x00AC: 0xAA, 0x00BD: 0xAB,
  0x00BC: 0xAC, 0x00A1: 0xAD, 0x00AB: 0xAE, 0x00BB: 0xAF,
  0x00C1: 0xB5, 0x00C2: 0xB6, 0x00C0: 0xB7, 0x00A9: 0xB8,
  0x00A2: 0xBD, 0x00A5: 0xBE, 0x00E3: 0xC6, 0x00C3: 0xC7,
  0x00A4: 0xCF, 0x00F0: 0xD0, 0x00D0: 0xD1, 0x00CA: 0xD2,
  0x00CB: 0xD3, 0x00C8: 0xD4, 0x00CD: 0xD6,
  0x00CE: 0xD7, 0x00CF: 0xD8, 0x00A6: 0xDD, 0x00CC: 0xDE,
  0x00D3: 0xE0, 0x00DF: 0xE1, 0x00D4: 0xE2, 0x00D2: 0xE3,
  0x00F5: 0xE4, 0x00D5: 0xE5, 0x00B5: 0xE6, 0x00FE: 0xE7,
  0x00DE: 0xE8, 0x00DA: 0xE9, 0x00DB: 0xEA, 0x00D9: 0xEB,
  0x00FD: 0xEC, 0x00DD: 0xED, 0x00AF: 0xEE, 0x00B4: 0xEF,
  0x00B1: 0xF1, 0x00BE: 0xF3, 0x00B6: 0xF4, 0x00A7: 0xF5,
  0x00F7: 0xF6, 0x00B8: 0xF7, 0x00B0: 0xF8, 0x00A8: 0xF9,
  0x00B7: 0xFA, 0x00B9: 0xFB, 0x00B3: 0xFC, 0x00B2: 0xFD,
};

// Pontuação tipográfica (aspas curvas, travessões, reticências, NBSP) que
// costuma vir colada de catálogos/Word/WhatsApp. CP850 não tem esses glifos —
// mapeia pro equivalente ASCII pra não virar "?".
const PUNCT_FALLBACK: Record<number, number> = {
  0x00A0: 0x20,               // NBSP → espaço normal
  0x2018: 0x27, 0x2019: 0x27, // ' '  → '
  0x201A: 0x27,               // ‚    → '
  0x201C: 0x22, 0x201D: 0x22, // " "  → "
  0x201E: 0x22,               // „    → "
  0x2013: 0x2D, 0x2014: 0x2D, // – —  → -
  0x2015: 0x2D,               // ―    → -
  0x2022: 0x2D,               // •    → -
  0x2026: 0x2E,               // …    → .
};

const DIACRITIC_FALLBACK: Record<number, string> = {
  0x0100: "A", 0x0101: "a", 0x0102: "A", 0x0103: "a", 0x0104: "A", 0x0105: "a",
  0x0106: "C", 0x0107: "c", 0x010C: "C", 0x010D: "c",
  0x010E: "D", 0x010F: "d", 0x0110: "D", 0x0111: "d",
  0x0112: "E", 0x0113: "e", 0x0118: "E", 0x0119: "e", 0x011A: "E", 0x011B: "e",
  0x011E: "G", 0x011F: "g",
  0x0128: "I", 0x0129: "i", 0x012A: "I", 0x012B: "i", 0x012C: "I", 0x012D: "i",
  0x0141: "L", 0x0142: "l",
  0x0143: "N", 0x0144: "n", 0x0147: "N", 0x0148: "n",
  0x014C: "O", 0x014D: "o", 0x014E: "O", 0x014F: "o",
  0x0158: "R", 0x0159: "r",
  0x015A: "S", 0x015B: "s", 0x015E: "S", 0x015F: "s",
  0x0162: "T", 0x0163: "t", 0x0164: "T", 0x0165: "t",
  0x016A: "U", 0x016B: "u", 0x016E: "U", 0x016F: "u", 0x0170: "U", 0x0171: "u",
  0x0179: "Z", 0x017A: "z", 0x017B: "Z", 0x017C: "z",
};

function encodeCP850Char(cp: number): number {
  if (cp <= 0x7F) return cp;
  const mapped = UNICODE_TO_CP850[cp];
  if (mapped !== undefined) return mapped;
  const punct = PUNCT_FALLBACK[cp];
  if (punct !== undefined) return punct;
  const fallback = DIACRITIC_FALLBACK[cp];
  if (fallback !== undefined) return fallback.charCodeAt(0);
  return 0x3F;
}

export function text(str: string): Uint8Array {
  const normalized = str.normalize("NFC");
  const out: number[] = [];
  for (let i = 0; i < normalized.length; i++) {
    const cp = normalized.codePointAt(i)!;
    if (cp > 0xFFFF) {
      out.push(0x3F);
      i++;
      continue;
    }
    out.push(encodeCP850Char(cp));
  }
  return new Uint8Array(out);
}

export function line(str: string): Uint8Array {
  return concat(text(str), ESC_POS.LF);
}

export function separator(width: PaperWidth, char = "-"): Uint8Array {
  return line(char.repeat(getCharsPerLine(width)));
}

// Linha separadora do cupom (só "-" ou só "*"), nos modos de texto e imagem: na letra alta ela fica na altura normal.
export function linhaSeparadora(texto: string): boolean {
  return texto.length >= 10 && /^(-+|\*+)$/.test(texto);
}

// Quebra texto em N linhas respeitando palavras (preferencialmente no
// espaço). Palavras maiores que cols são cortadas em pedaços. Versão
// "raw" (retorna string[]) pra ser reusada pelo padLine sem virar bytes.
export function wrapWords(str: string, cols: number): string[] {
  if (str.length <= cols) return [str];
  const out: string[] = [];
  let current = "";
  for (const word of str.split(" ")) {
    if (word.length > cols) {
      if (current) { out.push(current); current = ""; }
      for (let i = 0; i < word.length; i += cols) {
        out.push(word.slice(i, i + cols));
      }
      continue;
    }
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > cols) {
      out.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) out.push(current);
  return out;
}

// Linha esquerda+direita (ex: "1x Produto" + "R$ 9,99") com padding entre.
// Quando o conteúdo não cabe em uma linha, quebra o lado esquerdo em palavras
// (NUNCA truncar — versão anterior fatiava com .slice, escondia o resto do
// nome do produto. Caso real: "1x 3 X-bacon Especial + Tuchaua 1,5l" virava
// "1x 3 X-bacon Especial +" em cupom 58mm, omitindo "Tuchaua 1,5l"). O lado
// direito (preço/total) é alinhado na ÚLTIMA linha se sobrar espaço, ou em
// linha própria à direita se não couber.
export function padLine(left: string, right: string, width: PaperWidth): Uint8Array {
  const cols = getCharsPerLine(width);
  // +1 garante pelo menos 1 espaço entre left e right.
  if (left.length + 1 + right.length <= cols) {
    const space = cols - left.length - right.length;
    return line(left + " ".repeat(space) + right);
  }
  const wrapped = wrapWords(left, cols);
  const lastIdx = wrapped.length - 1;
  const lastLine = wrapped[lastIdx];
  // Tenta encaixar o `right` na última linha do nome (com pelo menos 1 espaço).
  if (lastLine.length + 1 + right.length <= cols) {
    const space = cols - lastLine.length - right.length;
    wrapped[lastIdx] = lastLine + " ".repeat(space) + right;
    return concat(...wrapped.map((l) => line(l)));
  }
  // Senão, manda o nome inteiro + linha extra com o `right` à direita.
  const rightPad = Math.max(0, cols - right.length);
  wrapped.push(" ".repeat(rightPad) + right);
  return concat(...wrapped.map((l) => line(l)));
}

export function wrapText(str: string, width: PaperWidth): Uint8Array[] {
  const cols = getCharsPerLine(width);
  return wrapWords(str, cols).map((l) => line(l));
}

export function concat(...arrays: Uint8Array[]): Uint8Array {
  let totalLen = 0;
  for (const arr of arrays) totalLen += arr.length;
  const result = new Uint8Array(totalLen);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}

export function buildReceipt(...parts: (Uint8Array | Uint8Array[])[]): Uint8Array {
  const flat: Uint8Array[] = [];
  for (const p of parts) {
    if (Array.isArray(p)) flat.push(...p);
    else flat.push(p);
  }
  return concat(...flat);
}
